import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/user/Layout';
import { FileText, Award, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';

interface Survey {
  id: string;
  title: string;
  description: string;
  points: number;
  questions: any[];
  created_at: string;
  language?: string;
  translations?: Record<string, any>;
}

interface CompletedSurvey {
  id: string;
  survey_id: string;
  survey_title: string;
  points: number;
  completed_at: string;
}

const Surveys: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentLanguage } = useLanguage();
  const [availableSurveys, setAvailableSurveys] = useState<Survey[]>([]);
  const [completedSurveys, setCompletedSurveys] = useState<CompletedSurvey[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSurvey, setActiveSurvey] = useState<Survey | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});

  useEffect(() => {
    if (user) {
      fetchSurveys();
      fetchCompletedSurveys();
    }
  }, [user, currentLanguage]);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      
      // Set language for RLS policies
      await supabase.rpc('set_language', { lang: currentLanguage });
      
      // Get all surveys
      const { data: allSurveys, error: surveysError } = await supabase
        .from('surveys')
        .select('*')
        .eq('active', true)
        .order('created_at', { ascending: false });
      
      if (surveysError) throw surveysError;
      
      // Get completed survey IDs for this user
      const { data: completedData, error: completedError } = await supabase
        .from('completed_surveys')
        .select('survey_id')
        .eq('user_id', user?.id);
      
      if (completedError) throw completedError;
      
      // Filter out completed surveys
      const completedIds = completedData?.map(item => item.survey_id) || [];
      const available = allSurveys?.filter(survey => !completedIds.includes(survey.id)) || [];
      
      // Process surveys based on language
      const processedSurveys = available.map(survey => {
        if (survey.language === currentLanguage) {
          return survey;
        } else if (survey.translations && survey.translations[currentLanguage]) {
          return {
            ...survey,
            title: survey.translations[currentLanguage].title,
            description: survey.translations[currentLanguage].description,
            questions: survey.translations[currentLanguage].questions
          };
        }
        return null;
      }).filter(Boolean);
      
      setAvailableSurveys(processedSurveys);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      toast.error(t('errorFetchingSurveys'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedSurveys = async () => {
    try {
      const { data, error } = await supabase
        .from('completed_surveys')
        .select('*, surveys(title)')
        .eq('user_id', user?.id)
        .order('completed_at', { ascending: false });
      
      if (error) throw error;
      
      if (data) {
        const formatted = data.map(item => ({
          id: item.id,
          survey_id: item.survey_id,
          survey_title: item.surveys.title,
          points: item.points,
          completed_at: item.completed_at
        }));
        
        setCompletedSurveys(formatted);
      }
    } catch (error) {
      console.error('Error fetching completed surveys:', error);
    }
  };

  const startSurvey = (survey: Survey) => {
    setActiveSurvey(survey);
    setCurrentQuestion(0);
    setAnswers({});
  };

  const handleAnswer = (questionId: string, answer: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const nextQuestion = () => {
    if (currentQuestion < activeSurvey!.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      completeSurvey();
    }
  };

  const completeSurvey = async () => {
    if (!activeSurvey || !user) return;
    
    try {
      setLoading(true);

      // Start a Supabase transaction
      const { data: { user: { id: userId } }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Save survey responses
      const { error: responsesError } = await supabase
        .from('survey_responses')
        .insert([
          {
            user_id: userId,
            survey_id: activeSurvey.id,
            responses: answers
          }
        ]);
      
      if (responsesError) throw responsesError;
      
      // Mark survey as completed and add points
      const { error: completedError } = await supabase
        .from('completed_surveys')
        .insert([
          {
            user_id: userId,
            survey_id: activeSurvey.id,
            points: activeSurvey.points
          }
        ]);
      
      if (completedError) throw completedError;
      
      // Add points to user
      const { error: pointsError } = await supabase
        .from('user_points')
        .insert([
          {
            user_id: userId,
            points: activeSurvey.points,
            source: 'survey',
            description: `Completed survey: ${activeSurvey.title}`
          }
        ]);
      
      if (pointsError) throw pointsError;
      
      toast.success(t('surveyCompleted', { points: activeSurvey.points }));
      
      // Refresh data
      await Promise.all([
        fetchSurveys(),
        fetchCompletedSurveys()
      ]);
      
      // Reset active survey
      setActiveSurvey(null);
    } catch (error) {
      console.error('Error completing survey:', error);
      toast.error(t('errorCompletingSurvey'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  // Render survey question based on type
  const renderQuestion = (question: any) => {
    switch (question.type) {
      case 'multiple_choice':
        return (
          <div>
            <h4 className="text-lg font-medium mb-4">{question.text}</h4>
            <div className="space-y-2">
              {question.options.map((option: string, index: number) => (
                <label key={index} className="flex items-center space-x-3">
                  <input
                    type="radio"
                    name={question.id}
                    value={option}
                    checked={answers[question.id] === option}
                    onChange={() => handleAnswer(question.id, option)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );
      
      case 'text':
        return (
          <div>
            <h4 className="text-lg font-medium mb-4">{question.text}</h4>
            <textarea
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswer(question.id, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={4}
            />
          </div>
        );
      
      case 'rating':
        return (
          <div>
            <h4 className="text-lg font-medium mb-4">{question.text}</h4>
            <div className="flex space-x-4 justify-center">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => handleAnswer(question.id, rating)}
                  className={`h-12 w-12 rounded-full flex items-center justify-center text-lg font-medium ${
                    answers[question.id] === rating
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {rating}
                </button>
              ))}
            </div>
          </div>
        );
      
      case 'boolean':
        return (
          <div>
            <h4 className="text-lg font-medium mb-4">{question.text}</h4>
            <div className="flex space-x-4 justify-center">
              <button
                type="button"
                onClick={() => handleAnswer(question.id, true)}
                className={`px-6 py-2 rounded-md ${
                  answers[question.id] === true
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('yes')}
              </button>
              <button
                type="button"
                onClick={() => handleAnswer(question.id, false)}
                className={`px-6 py-2 rounded-md ${
                  answers[question.id] === false
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('no')}
              </button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (activeSurvey) {
    const currentQuestionData = activeSurvey.questions[currentQuestion];
    const isLastQuestion = currentQuestion === activeSurvey.questions.length - 1;
    
    return (
      <Layout title={activeSurvey.title}>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">{t('question')} {currentQuestion + 1} / {activeSurvey.questions.length}</h3>
              <div className="text-sm text-gray-500">
                {t('pointsWorth')}: <span className="font-medium text-blue-600">{activeSurvey.points} {t('points')}</span>
              </div>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full" 
                style={{ width: `${((currentQuestion + 1) / activeSurvey.questions.length) * 100}%` }}
              ></div>
            </div>
          </div>
          
          <div className="mb-8">
            {renderQuestion(currentQuestionData)}
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={nextQuestion}
              disabled={!answers[currentQuestionData.id]}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isLastQuestion ? t('completeSurvey') : t('nextQuestion')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={t('surveys')}>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">{t('availableSurveys')}</h3>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : availableSurveys.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t('noSurveysAvailable')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {availableSurveys.map((survey) => (
              <div key={survey.id} className="border rounded-lg overflow-hidden shadow-sm">
                <div className="p-5">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center">
                      <FileText className="h-10 w-10 text-blue-600 mr-3" />
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{survey.title}</h4>
                        <p className="text-sm text-gray-500">{survey.description}</p>
                      </div>
                    </div>
                    <div className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-sm font-medium flex items-center">
                      <Award className="h-4 w-4 mr-1" />
                      {survey.points} {t('points')}
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <button
                      onClick={() => startSurvey(survey)}
                      className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                      {t('startSurvey')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">{t('completedSurveys')}</h3>
        
        {completedSurveys.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t('noCompletedSurveys')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('survey')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('points')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('completedDate')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {completedSurveys.map((survey) => (
                  <tr key={survey.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-blue-600 mr-2" />
                        <div className="text-sm font-medium text-gray-900">{survey.survey_title}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <Award className="h-4 w-4 text-blue-600 mr-1" />
                        {survey.points}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(survey.completed_at)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Surveys;